import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchUsdToCurrencyRate } from "../api/fx.js";
import {
  currencyOptions,
  currencyForCountry,
  getDisplayCurrency,
  persistDisplayCurrency,
  selectionLabel,
} from "../utils/displayCurrency.js";
import { isTouchDevice } from "../utils/viewport.js";

/**
 * Header control that picks the ship-to country whose currency the catalog is
 * priced in. Choosing one fetches the USD rate, stores it, and reloads — the
 * reload is what guarantees every price on the page redraws in the new
 * currency at once (see utils/displayCurrency.js).
 */
export default function CurrencySelector({ onPointerEnter, onPointerLeave }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null); // country code being fetched
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selection = getDisplayCurrency();
  const options = useMemo(() => currencyOptions(), []);

  // Matches on country name OR currency code, so "japan", "jpy" and "¥" all
  // find the same row — people reach for whichever they think in. Accents are
  // stripped so "aland" finds "Åland Islands".
  const normalise = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  // Searching a currency code matches every country that uses it — "usd"
  // returns nine — so matches are ranked rather than left alphabetical, which
  // buried UNITED STATES at the bottom under BRITISH INDIAN OCEAN TERRITORY.
  //
  // The home-country test leans on ISO 4217: a currency code is nearly always
  // its ISO 3166 country code plus a letter for the currency's name, so
  // USD->US, GBP->GB, JPY->JP. Shared codes with no country behind them (EUR,
  // the XCD/XOF group) simply match nobody and those lists stay alphabetical,
  // which is the right answer when no single country owns the currency.
  const score = (o, q) => {
    const name = normalise(o.name);
    const code = normalise(o.currency);
    let s = 0;
    if (code === q) s += 100;                                   // typed a currency code
    if (code === q && code.slice(0, 2) === o.country.toLowerCase()) s += 50; // ...its home
    if (name.startsWith(q)) s += 40;                            // typed the country
    else if (code.startsWith(q)) s += 20;
    else if (name.includes(q)) s += 10;
    return s;
  };

  const shown = useMemo(() => {
    const q = normalise(query.trim());
    if (!q) return options;
    return options
      .filter((o) =>
        normalise(o.name).includes(q)
        || normalise(o.currency).includes(q)
        || (o.symbol || "").toLowerCase().includes(q)
      )
      // Alphabetical within a tier — `options` arrives sorted by name, and
      // sort is stable, so equal scores keep that order.
      .sort((a, b) => score(b, q) - score(a, q));
  }, [options, query]);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      // Escape clears a query first and only closes on a second press, so a
      // mistyped search does not cost you the whole menu.
      if (e.key !== "Escape") return;
      setQuery((q) => {
        if (q) return "";
        setOpen(false);
        return q;
      });
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Reopening always starts from the full list, and the caret lands in the
  // search box so you can just type the country.
  useEffect(() => {
    if (!open) {
      setError(null);
      setQuery("");
      return;
    }
    // Not on a touchscreen: focusing there throws the keyboard up over the
    // list the visitor opened the menu to scroll. A tap on the field is one
    // step away when they do want to type.
    if (!isTouchDevice()) searchRef.current?.focus();
  }, [open]);

  const choose = useCallback(async (country) => {
    const currency = currencyForCountry(country);
    if (!currency) return;
    setError(null);

    if (currency === getDisplayCurrency().currency) {
      // Same currency (e.g. another euro country) — record the country so the
      // label is right, but there is nothing to re-price and no need to reload.
      persistDisplayCurrency({ ...getDisplayCurrency(), country });
      setOpen(false);
      return;
    }

    setPending(country);
    try {
      const { rate } = await fetchUsdToCurrencyRate(currency);
      persistDisplayCurrency({ country, currency, rate, fetchedAt: Date.now() });
      // Full reload: every price is formatted from the module-level selection,
      // so a reload is the one thing that guarantees the whole page agrees.
      window.location.reload();
    } catch {
      // Never guess a rate — a wrong one misprices the entire catalog.
      setPending(null);
      setError(`No live rate for ${currency}. Still showing USD.`);
    }
  }, []);

  return (
    <div className="currency-select" ref={rootRef}>
      <button
        type="button"
        className={`currency-select__button${open ? " currency-select__button--open" : ""}`}
        data-header-interaction="currency"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Currency: ${selectionLabel(selection)}. Change country`}
        onClick={() => setOpen((v) => !v)}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        <span className="currency-select__label">{selectionLabel(selection)}</span>
        {/* Dropdown affordance: hidden until the site header is hovered (or
            the menu is open), then flips to point up. See app.css. */}
        <svg
          className="currency-select__chevron"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="currency-select__menu">
          {error && <p className="currency-select__error">{error}</p>}
          <div className="currency-select__search">
            <input
              ref={searchRef}
              type="text"
              className="currency-select__search-input"
              placeholder="Search country or currency"
              aria-label="Search country or currency"
              value={query}
              autoComplete="off"
              spellCheck="false"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Enter takes the only remaining match, so a full search can be
                // finished from the keyboard without reaching for the list.
                if (e.key === "Enter" && shown.length > 0 && pending === null) {
                  e.preventDefault();
                  choose(shown[0].country);
                }
              }}
            />
          </div>
          {shown.length === 0 ? (
            <p className="currency-select__empty">No country or currency matches “{query}”.</p>
          ) : (
          <ul className="currency-select__list" role="listbox" aria-label="Ship-to country">
            {shown.map((o) => {
              const active = o.country === selection.country;
              return (
                <li key={o.country}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    disabled={pending !== null}
                    className={`currency-select__option${active ? " currency-select__option--active" : ""}`}
                    onClick={() => choose(o.country)}
                  >
                    {pending === o.country
                      ? `${o.name} …`
                      : `${o.name} (${o.currency} ${o.symbol})`}
                  </button>
                </li>
              );
            })}
          </ul>
          )}
        </div>
      )}
    </div>
  );
}
